import { useEffect, useState, useCallback } from 'react';
import { Tv, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Circle, ChevronLeft, Home, Play } from 'lucide-react';
import { toast } from 'sonner';

// Standard Android KeyEvent codes -- stable since early Android API levels.
const KEYS = {
  UP: 19, DOWN: 20, LEFT: 21, RIGHT: 22, CENTER: 23,
  BACK: 4, HOME: 3, PLAY_PAUSE: 85,
};

const isAndroid = typeof window !== 'undefined' && !!window.QudeAndroid;

/**
 * Network-ADB Fire TV control -- connects from the phone over the LAN, no
 * app installed on the Fire TV itself. Requires enabling ADB debugging once
 * in the Fire TV's Developer Options, and accepting an on-screen prompt
 * (using the actual Fire TV remote) the very first time this phone connects.
 *
 * All the real work happens natively in FireTvAdbClient.kt via the
 * QudeAndroid bridge -- this page is just status polling + button taps,
 * same pattern Settings.js already uses for permission status polling.
 */
export default function FireTVAdbControl() {
  const [ip, setIp] = useState('');
  const [status, setStatus] = useState('disconnected'); // disconnected | connecting | connected | failed

  const refreshStatus = useCallback(() => {
    if (!isAndroid || !window.QudeAndroid.getFireTvAdbStatus) return;
    try {
      const s = window.QudeAndroid.getFireTvAdbStatus();
      setStatus(s || 'disconnected');
      if (s === 'connected') {
        const savedIp = window.QudeAndroid.getFireTvAdbIp?.();
        if (savedIp) setIp(savedIp);
      }
    } catch {}
  }, []);

  useEffect(() => {
    refreshStatus();
    const interval = setInterval(refreshStatus, 1500);
    return () => clearInterval(interval);
  }, [refreshStatus]);

  const connect = () => {
    if (!ip.trim()) { toast.error('Enter your Fire TV\'s IP address'); return; }
    if (!isAndroid || !window.QudeAndroid.connectFireTvAdb) {
      toast.error('Fire TV control requires the Qude Android app');
      return;
    }
    window.QudeAndroid.connectFireTvAdb(ip.trim());
    setStatus('connecting');
    toast('📺 Check your Fire TV — accept the debugging prompt with your remote if this is the first time.', { duration: 6000 });
  };

  const disconnect = () => {
    if (!isAndroid || !window.QudeAndroid.disconnectFireTvAdb) return;
    window.QudeAndroid.disconnectFireTvAdb();
    setStatus('disconnected');
  };

  const sendKey = (code) => {
    if (!isAndroid || !window.QudeAndroid.sendFireTvAdbKey) return;
    window.QudeAndroid.sendFireTvAdbKey(code);
  };

  const btnStyle = "w-14 h-14 rounded-full bg-white/8 hover:bg-white/15 active:scale-95 flex items-center justify-center transition-all border border-white/10";

  return (
    <div className="max-w-lg mx-auto pb-20 px-1">
      <div style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)', paddingBottom: '8px' }}>
        <h1 className="text-xl sm:text-2xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>Fire TV</h1>
        <p className="text-sm text-neutral-500 mt-1">Control and detect what's playing over your network — no app to install on the TV.</p>
      </div>

      {!isAndroid && (
        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-amber-300 mb-4">
          Fire TV control only works from the Qude Android app.
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-5 mb-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0">
            <Tv className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <p className="text-sm font-bold text-white">
              {status === 'connected' ? 'Connected' : status === 'connecting' ? 'Connecting…' : status === 'failed' ? "Couldn't connect" : 'Not connected'}
            </p>
            {status === 'connected' && ip && <p className="text-xs text-neutral-500">{ip}</p>}
          </div>
        </div>

        {status !== 'connected' && (
          <>
            <input
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              placeholder="Fire TV IP address (e.g. 192.168.1.42)"
              className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm outline-none mb-3"
            />
            <button
              onClick={connect}
              disabled={status === 'connecting'}
              className="w-full py-2.5 rounded-full text-sm font-bold text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg,#f97316,#ea580c)' }}>
              {status === 'connecting' ? 'Connecting…' : 'Connect'}
            </button>
            <div className="mt-4 text-xs text-neutral-500 leading-relaxed space-y-1.5">
              <p className="font-semibold text-neutral-400">On your Fire TV, once:</p>
              <p>1. Settings → My Fire TV → Developer Options</p>
              <p>2. Turn on "ADB debugging"</p>
              <p>3. Note the IP address under Settings → My Fire TV → About → Network</p>
              <p>4. After tapping Connect, accept the prompt on your TV screen using your Fire TV remote</p>
            </div>
          </>
        )}

        {status === 'connected' && (
          <button onClick={disconnect} className="w-full py-2 rounded-full text-xs font-semibold text-neutral-400 border border-white/10">
            Disconnect
          </button>
        )}
      </div>

      {status === 'connected' && (
        <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 flex flex-col items-center gap-3">
          <button className={btnStyle} onClick={() => sendKey(KEYS.UP)}><ArrowUp className="w-5 h-5 text-white" /></button>
          <div className="flex items-center gap-3">
            <button className={btnStyle} onClick={() => sendKey(KEYS.LEFT)}><ArrowLeft className="w-5 h-5 text-white" /></button>
            <button className={`${btnStyle} bg-white/15`} onClick={() => sendKey(KEYS.CENTER)}><Circle className="w-5 h-5 text-white" fill="white" /></button>
            <button className={btnStyle} onClick={() => sendKey(KEYS.RIGHT)}><ArrowRight className="w-5 h-5 text-white" /></button>
          </div>
          <button className={btnStyle} onClick={() => sendKey(KEYS.DOWN)}><ArrowDown className="w-5 h-5 text-white" /></button>
          <div className="flex items-center gap-4 mt-3">
            <button className={btnStyle} onClick={() => sendKey(KEYS.BACK)}><ChevronLeft className="w-5 h-5 text-white" /></button>
            <button className={btnStyle} onClick={() => sendKey(KEYS.HOME)}><Home className="w-5 h-5 text-white" /></button>
            <button className={btnStyle} onClick={() => sendKey(KEYS.PLAY_PAUSE)}><Play className="w-5 h-5 text-white" fill="white" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
